import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../utils/firebase';
import { Study } from '../../types/study.types';

interface APIResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface EnrollmentResult {
  participantId: string;
  participantCode: string;
}

const StudyEnrollment: React.FC = () => {
  const { studyId } = useParams<{ studyId: string }>();

  // Study data
  const [study, setStudy] = useState<Study | null>(null);
  const [studyLoading, setStudyLoading] = useState(true);
  const [studyError, setStudyError] = useState<string | null>(null);

  // Form state
  const [participantCode, setParticipantCode] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [consentAcknowledged, setConsentAcknowledged] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollmentResult, setEnrollmentResult] = useState<EnrollmentResult | null>(null);

  // Form validation
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fetch study details
  useEffect(() => {
    const fetchStudy = async () => {
      if (!studyId) {
        setStudyError('Invalid enrollment link');
        setStudyLoading(false);
        return;
      }

      try {
        const studyDoc = await getDoc(doc(db, 'studies', studyId));

        if (!studyDoc.exists()) {
          setStudyError('Study not found. Please check your enrollment link.');
          setStudyLoading(false);
          return;
        }

        const studyData = { ...studyDoc.data(), id: studyDoc.id } as Study;

        // Check if study is active and enrollment is open
        if (studyData.status !== 'active') {
          setStudyError('This study is not currently accepting new participants.');
          setStudyLoading(false);
          return;
        }

        if (!studyData.enrollmentSettings?.isOpen) {
          setStudyError('Enrollment for this study is currently closed.');
          setStudyLoading(false);
          return;
        }

        setStudy(studyData);
        setStudyLoading(false);
      } catch (err: any) {
        console.error('Error fetching study:', err);
        setStudyError('Failed to load study information. Please try again later.');
        setStudyLoading(false);
      }
    };

    fetchStudy();
  }, [studyId]);

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Participant code validation
    if (!participantCode.trim()) {
      errors.participantCode = 'Participant code is required';
    } else if (study?.type === 'observational') {
      const prefix = study.codeSettings?.prefix || '';
      const codeRegex = new RegExp(`^${prefix}-\\d{3,}$`, 'i');
      if (!codeRegex.test(participantCode.trim().toUpperCase())) {
        errors.participantCode = `Invalid code format. Expected format: ${prefix}-001`;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      errors.email = 'Email address is required';
    } else if (!emailRegex.test(email.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    // Confirm email validation
    if (!confirmEmail.trim()) {
      errors.confirmEmail = 'Please confirm your email address';
    } else if (email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase()) {
      errors.confirmEmail = 'Email addresses do not match';
    }

    // Consent validation
    if (!consentAcknowledged) {
      errors.consent = 'You must acknowledge that informed consent was obtained';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!studyId) {
      setSubmitError('Invalid study ID');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const enrollParticipant = httpsCallable<
        { studyId: string; participantCode: string; email: string; consentAcknowledged: boolean },
        APIResponse<EnrollmentResult>
      >(functions, 'enrollParticipant');

      const result = await enrollParticipant({
        studyId,
        participantCode: participantCode.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
        consentAcknowledged,
      });

      if (result.data.success && result.data.data) {
        setIsEnrolled(true);
        setEnrollmentResult(result.data.data);
      } else {
        setSubmitError(result.data.error || 'Enrollment failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Enrollment error:', err);

      // Extract error message from Firebase error
      let errorMessage = 'Enrollment failed. Please try again.';
      if (err.code === 'functions/invalid-argument') {
        errorMessage = err.message || 'Invalid participant code or email';
      } else if (err.code === 'functions/already-exists') {
        errorMessage = 'This participant code has already been used';
      } else if (err.code === 'functions/not-found') {
        errorMessage = 'Study not found or participant code is invalid';
      } else if (err.code === 'functions/failed-precondition') {
        errorMessage = err.message || 'Enrollment is not currently available';
      } else if (err.code === 'functions/resource-exhausted') {
        errorMessage = 'Study enrollment is full';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (studyLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading study information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (studyError) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to Enroll</h1>
            <p className="text-gray-600 mb-6">{studyError}</p>
            <Link
              to="/"
              className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isEnrolled && enrollmentResult) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Successfully Enrolled!</h1>
              <p className="text-gray-600 mb-6">
                You have been enrolled in <span className="font-semibold">{study?.title}</span>
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">Your Participant Code</p>
              <p className="text-2xl font-mono font-bold text-gray-900">{enrollmentResult.participantCode}</p>
              <p className="text-xs text-gray-500 mt-2">Please keep this code for your records</p>
            </div>

            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>A welcome email has been sent to <strong>{email}</strong></span>
              </div>
              <div className="flex items-start">
                <svg className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>You will receive survey links at scheduled timepoints</span>
              </div>
              <div className="flex items-start">
                <svg className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Your data will be kept confidential and de-identified</span>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                Thank you for participating in this study.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Enrollment form
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        {/* Study Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{study?.title}</h1>
          {study?.description && (
            <p className="mt-2 text-gray-600">{study.description}</p>
          )}
        </div>

        {/* Enrollment Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Study Enrollment</h2>

          {submitError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-700">{submitError}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Participant Code */}
            <div>
              <label htmlFor="participantCode" className="block text-sm font-medium text-gray-700 mb-1">
                Participant Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="participantCode"
                value={participantCode}
                onChange={(e) => setParticipantCode(e.target.value.toUpperCase())}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 font-mono ${
                  formErrors.participantCode ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={study?.codeSettings?.prefix ? `${study.codeSettings.prefix}-001` : 'Enter your code'}
                disabled={isSubmitting}
              />
              {formErrors.participantCode && (
                <p className="mt-1 text-sm text-red-600">{formErrors.participantCode}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Enter the code provided by your study coordinator
              </p>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  formErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="your.email@example.com"
                disabled={isSubmitting}
              />
              {formErrors.email && (
                <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
              )}
            </div>

            {/* Confirm Email */}
            <div>
              <label htmlFor="confirmEmail" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="confirmEmail"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  formErrors.confirmEmail ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Confirm your email"
                disabled={isSubmitting}
              />
              {formErrors.confirmEmail && (
                <p className="mt-1 text-sm text-red-600">{formErrors.confirmEmail}</p>
              )}
            </div>

            {/* Consent Acknowledgment */}
            <div className="pt-2">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentAcknowledged}
                  onChange={(e) => setConsentAcknowledged(e.target.checked)}
                  className="mt-1 h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  disabled={isSubmitting}
                />
                <span className="ml-3 text-sm text-gray-600">
                  I confirm that I have provided written informed consent to participate in this study
                  and understand the study procedures, risks, and my right to withdraw at any time.
                  <span className="text-red-500"> *</span>
                </span>
              </label>
              {formErrors.consent && (
                <p className="mt-1 text-sm text-red-600 ml-7">{formErrors.consent}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-teal-600 hover:bg-teal-700'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enrolling...
                  </span>
                ) : (
                  'Enroll in Study'
                )}
              </button>
            </div>
          </form>

          {/* Privacy Notice */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-start text-xs text-gray-500">
              <svg className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p>
                Your email address is used only to send you survey links and study updates.
                All survey responses are stored with your participant code only and cannot be linked back to your identity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyEnrollment;
